import {
  COLOR_SCALE_20,
  COLOR_SCALE_80,
  COLOR_SCALE_NEGATIVE_10,
  COLOR_SCALE_NEGATIVE_50,
} from '../colors';

import { formatData } from '../data_format';

/**
 * formats the data labels for Bar Chart with Two Data Labels
 * @param  {Object} format
 * @param  {Object} value
 * @param  {Object} yDelta
 */
export const twoDataLabelFormatter = ({ format, value, yDelta }) => {
  const defaultLabel = `<div style="
    border-radius: 5px; 
    color:  var(--color-text-default);
    font-size: 12px; 
    padding: 5px; 
    text-align: center;
    width: 40px; 
  ">
    ${formatData({ format, value })}
  </div>`;

  const isYDeltaPositive = yDelta > 0;

  if (value === 'INF_SENTINEL' || value === 'NAN_SENTINEL') {
    return `<div style="
      background-color: var(--color-background-light); 
      border-radius: 5px; 
      color:  var(--color-text-default);
      font-size: 12px; 
      padding: 5px; 
      text-align: center;
      width: 40px;
    ">
      -
    </div>`;
  }

  if (typeof yDelta === 'number' && yDelta !== 0) {
    return `<div style="
      background-color: ${isYDeltaPositive ? COLOR_SCALE_20 : COLOR_SCALE_NEGATIVE_10}; 
      border-radius: 5px; 
      color:  ${isYDeltaPositive ? COLOR_SCALE_80 : COLOR_SCALE_NEGATIVE_50}; 
      font-size: 12px; 
      padding: 5px; 
      text-align: center;
      width: 40px;
    ">
      ${formatData({ format, value })}
    </div>`;
  }

  return defaultLabel;
};

/**
 * formats the chart tooltip
 * @param  {Object} data
 * @param  {String} format
 * @return {Node}
 */
export const tooltipFormatter = ({ data, format }) => {
  return `<b>${data.category}</b><br/><span style="color:${data.color}">\u25CF</span> ${
    data.series.name
  }: <b>${formatData({ format: format, value: data.y })}</b>
  ${data.sampleSize ? `<br/>Sample Size${data.sampleSizeRowLevel ? ' (entire row)' : ''}: <b>${formatData({ value: data.sampleSize })}</b>` : ''}
  ${data.sampleSizePrior ? `<br/>Sample Size (Prev): <b>${formatData({ value: data.sampleSizePrior })}</b>` : ''}`;
};

/**
 * formats the chart tooltip for column range values (high / low)
 * @param  {Object} data
 * @param  {String} format
 * @return {Node}
 */
export const tooltipRangeFormatter = ({ data, format }) => {
  return `<b>${data.category}</b><br/><span style="color:${data.color}">\u25CF</span> ${
    data.series.name
  }: <b>${formatData({ format: format, value: Math.abs(data.high - data.low) })}</b>`;
};

/**
 * formats the chart tooltip for multiple series
 * @param  {Object} data
 * @param  {String} format
 * @return {Node}
 */
export const tooltipMultiPointFormatter = ({ data, format }) => {
  let tooltipContent = [];

  data?.points?.forEach((point, i) => {
    if (point?.series?.type !== 'arearange') {
      let content = `<span style="color:${point.color}">\u25CF</span>${
        point?.series?.name
      }: <b>${formatData({ format, value: point.y })}</b>`;

      const nextPoint = data?.points?.[i + 1];
      if (nextPoint && nextPoint?.point?.low && nextPoint?.point?.high) {
        content += `(Low: <b>${formatData({
          format,
          value: nextPoint.point.low,
        })}</b>, High: <b>${formatData({ format, value: nextPoint.point.high })}</b>)`;
      }
      tooltipContent.push(content);
    }
  });

  return `<b>${data.category}</b><br/>` + tooltipContent.join('<br/>');
};

/**
 * formats the chart tooltip for scatter charts with x and y values
 * @param  {Object} data
 * @param  {Object} xAxis
 * @param  {Object} yAxis
 * @return {Node}
 */
export const tooltipFormatterxy = ({ data, xAxis, yAxis }) => {
  return (
    `<span style="color:${data?.color}">\u25cf</span><span style="font-weight: bold"> ${data.name}</span><br/>` +
    `${xAxis?.title?.text}: <span style="font-weight: bold">${formatData({ format: xAxis?.labels?.format, value: data.x })}</span><br/ >` +
    `${yAxis?.title?.text}: <span style="font-weight: bold">${formatData({ format: yAxis?.labels?.format, value: data.y })}</span>` +
    `${data.sampleSize ? `<br/>Sample Size: <span style="font-weight: bold">${formatData({ value: data.sampleSize })}</span>` : ''}`
  );
};
